import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import LeadDialog from '../../Components/LeadDialog';
import LeadAccordion from '../../Components/LeadAccordion';
import { handleValidateCodevalue, dateTimeISTFormatter } from '../../Utility/constant';
import { formatDuration } from '../../Utility/calculations';
import StepResultRenderer from './StepResultRenderer';
import { formatCamelCaseToTitle } from '../../Utility/workflowConfig';
import { StyledSubHeader } from '../../Components/Styled';
import './workflow.css';

const StepCard = ({ step, index, internationalise }) => {
    const statusStr = step.status ? handleValidateCodevalue(internationalise, step.status) || step.status : '';

    const headerLabel = (
        <Box className="workflow-dialog-header-box">
            <Box className="workflow-dialog-header-content">
                <Typography component="span" variant="caption" className="workflow-header-grey">
                    [{step.stepType}]
                </Typography>
                <Typography component="span" variant="body2" className="workflow-header-dark">
                    {step.stepName}
                </Typography>
                <Typography component="span" variant="body2" className="workflow-header-grey">
                    &nbsp;-&nbsp;
                </Typography>
                <Typography component="span" variant="body2" className="workflow-header-grey">
                    {statusStr}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <LeadAccordion
            label={headerLabel}
            defaultOpen={false}
        >
            <Box className="workflow-dialog-card-times">
                <Typography variant="body2" className="workflow-field-container">
                    <strong className="workflow-field-label">Started At:</strong> {step.startedAt ? dateTimeISTFormatter(step.startedAt) : '-'}
                </Typography>
                <Typography variant="body2" className="workflow-field-container">
                    <strong className="workflow-field-label">Ended At:</strong> {step.endedAt ? dateTimeISTFormatter(step.endedAt) : '-'}
                </Typography>
                <Typography variant="body2" className="workflow-field-container">
                    <strong className="workflow-field-label">Duration:</strong> {formatDuration(step.durationMs)}
                </Typography>
            </Box>

            {step.inputJson && Object.keys(step.inputJson).length > 0 && (
                <Box className="workflow-step-input-section">
                    <StyledSubHeader className="mb-10">
                        {step.stepType === 'PROCESS' ? 'Filters' : 'Input'}
                    </StyledSubHeader>
                    {Array.isArray(step.inputJson.filters) ? (
                        step.inputJson.filters.map(({ label, value }, i) => (
                            <Typography key={i} variant="body2" className="workflow-field-container-mt">
                                <span className="workflow-field-label">{label}:</span> {String(value)}
                            </Typography>
                        ))
                    ) : (
                        Object.entries(step.inputJson).map(([k, v]) => {
                            if (k === 'sampleRecords') return null;
                            return (
                                <Typography key={k} variant="body2" className="workflow-field-container-mt">
                                    <span className="workflow-field-label">{formatCamelCaseToTitle(k)}:</span> {String(v)}
                                </Typography>
                            );
                        })
                    )}
                </Box>
            )}

            <StepResultRenderer step={step} />
        </LeadAccordion>
    );
};

const StepDetailDialog = ({ open, onClose, logDetail }) => {
    const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);

    const steps = useMemo(() => logDetail?.executionJson?.steps || [], [logDetail]);

    const metaItems = useMemo(() => [
        { label: 'Status', value: logDetail?.status ?? '-' },
        { label: 'Started At', value: logDetail?.startedAt ? dateTimeISTFormatter(logDetail.startedAt) : '-' },
        { label: 'Ended At', value: logDetail?.endedAt ? dateTimeISTFormatter(logDetail.endedAt) : '-' },
        { label: 'Duration', value: formatDuration(logDetail?.durationMs) },
    ], [logDetail]);

    const contextItems = useMemo(() => [
        { label: 'Entity Type', value: logDetail?.entityType ?? '-' },
        { label: 'Trigger Type', value: logDetail?.triggerType ?? '-' },
    ].filter(({ value }) => value && value !== '-'), [logDetail]);

    if (!logDetail) return null;

    const runContextLabel = (
        <Box className="workflow-dialog-header-box">
            <Box className="workflow-dialog-header-content">
                <Typography component="span" variant="caption" className="workflow-header-grey">
                    [TRIGGER]
                </Typography>
                <Typography component="span" variant="body2" className="workflow-header-dark">
                    Run Context
                </Typography>
            </Box>
        </Box>
    );

    return (
        <LeadDialog
            open={open}
            handleClose={onClose}
            title={`${handleValidateCodevalue(internationalise, 'WF_LOG_DETAIL_TITLE')} - ${logDetail.workflowName}`}
            size="md"
            btnlabel=""
        >
            <Box className="workflow-dialog-meta-banner">
                {metaItems.map(({ label, value }) => (
                    <Typography key={label} variant="body2">
                        <strong >{label}:</strong>&nbsp;{value}
                    </Typography>
                ))}
            </Box>

            <LeadAccordion label={runContextLabel} defaultOpen={true}>
                <Box className="workflow-dialog-context-box">
                    {contextItems.map(({ label, value }) => (
                        <Typography key={label} variant="body2">
                            <strong className="workflow-field-label">{label}:</strong>&nbsp;{value}
                        </Typography>
                    ))}
                </Box>
            </LeadAccordion>

            <Box className="workflow-dialog-steps-container">
                {steps.length === 0 ? (
                    <Typography variant="body2" className="workflow-no-steps">
                        No steps recorded for this run.
                    </Typography>
                ) : (
                    steps.map((step, i) => (
                        <StepCard
                            key={`${step.stepOrder || i}-${step.stepName || 'step'}`}
                            step={step}
                            index={i}
                            internationalise={internationalise}
                        />
                    ))
                )}
            </Box>
        </LeadDialog>
    );
};

export default StepDetailDialog;
